import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { useNotificationContext } from "cerulean";
import ApiHttp from "byzantine/src/ApiHttp";
import { useACHImportContext } from "../ACHImportContext";
import { CATEGORY_KEYS } from "./recipientReviewConstants";
import { validateField, validateRow } from "./recipientReviewValidation";
import type {
  RowEdit,
  RowErrors,
  BulkUpdateRow,
  BulkUpdateErrorResponse,
} from "./recipientReviewTypes";

// eslint-disable-next-line import/prefer-default-export
export const useRecipientReview = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const { sendNotification } = useNotificationContext();
  const { nachaResponse, updateNachaRows, importType } =
    useACHImportContext();

  // Filter to rows with non_recipient_destination (new/hidden recipients)
  // and track their original indices in nachaResponse.rows
  const { newRecipientRows, originalIndices } = useMemo(() => {
    const rows: API.ACHImport.PaymentRow[] = [];
    const indices: number[] = [];
    nachaResponse?.rows.forEach((row, i) => {
      if (row.non_recipient_destination !== null) {
        rows.push(row);
        indices.push(i);
      }
    });
    return { newRecipientRows: rows, originalIndices: indices };
  }, [nachaResponse]);

  // --- Editable row state ---
  const [rowEdits, setRowEdits] = useState<Record<number, RowEdit>>(() => {
    const edits: Record<number, RowEdit> = {};
    newRecipientRows.forEach((row, idx) => {
      if (row.non_recipient_destination) {
        edits[idx] = { ...row.non_recipient_destination };
      }
    });
    return edits;
  });

  // --- Validation errors (merged backend field_errors + client-side) ---
  const [rowErrors, setRowErrors] = useState<Record<number, RowErrors>>(() => {
    const errors: Record<number, RowErrors> = {};
    newRecipientRows.forEach((row, idx) => {
      const rowErr: RowErrors = {};
      if (row.field_errors) {
        Object.assign(rowErr, row.field_errors);
      }
      const dest = row.non_recipient_destination;
      if (dest) {
        (
          [
            ["recipient_name", "name"],
            ["account_number", "account_number"],
            ["routing_number", "routing_number"],
            ["account_type", "account_type"],
          ] as [keyof typeof dest, string][]
        ).forEach(([destKey, errorKey]) => {
          if (!rowErr[errorKey]) {
            const error = validateField(l10n, errorKey, dest[destKey]);
            if (error) {
              rowErr[errorKey] = error;
            }
          }
        });
      }
      if (Object.keys(rowErr).length > 0) {
        errors[idx] = rowErr;
      }
    });
    return errors;
  });

  // --- Recipients created via auto-create ---
  const [rowRecipients, setRowRecipients] = useState<
    Record<number, API.Recipient>
  >(() => {
    const recipients: Record<number, API.Recipient> = {};
    newRecipientRows.forEach((row, idx) => {
      if (row.recipient) {
        recipients[idx] = row.recipient;
      }
    });
    return recipients;
  });

  // --- Dirty tracking ---
  const [dirtyRows, setDirtyRows] = useState<Set<number>>(new Set());

  // --- Auto-create in-flight tracking ---
  const autoCreatingRef = useRef<Set<number>>(new Set());

  // Selection state
  const [selected, setSelected] = useState<Set<number>>(
    () =>
      new Set(
        newRecipientRows
          .map((row, idx) => (row.recipient !== null ? idx : -1))
          .filter((idx) => idx !== -1)
      )
  );

  const [saved, setSaved] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState<Set<number>>(new Set());
  const [nextLoading, setNextLoading] = useState(false);

  // --- Derived state ---
  const hasAnyErrors = Object.values(rowErrors).some(
    (errs) => Object.keys(errs).length > 0
  );

  const errorCategories = useMemo(() => {
    const categories = new Set<string>();
    const requiredMsg = l10n.getString("recipient-review-validation-required");
    Object.values(rowErrors).forEach((errs) => {
      Object.entries(errs).forEach(([key, msg]) => {
        if (!CATEGORY_KEYS.includes(key)) return;
        if (msg === requiredMsg) {
          categories.add("required");
        } else {
          categories.add(key);
        }
      });
    });
    return Array.from(categories);
  }, [rowErrors, l10n]);

  const unsavedIdxs = newRecipientRows
    .map((_, idx) => idx)
    .filter((idx) => rowRecipients[idx] !== undefined && !saved.has(idx));

  const allSelected =
    unsavedIdxs.length > 0 && unsavedIdxs.every((idx) => selected.has(idx));
  const someSelected = selected.size > 0 && !allSelected;

  // --- Auto-create hidden recipient ---
  const autoCreateRecipient = useCallback(
    async (idx: number, edit: RowEdit) => {
      if (autoCreatingRef.current.has(idx)) return;
      autoCreatingRef.current.add(idx);

      try {
        const payload: { rows: BulkUpdateRow[] } = {
          rows: [
            {
              name: edit.recipient_name.trim(),
              type: "business",
              account_number: edit.account_number.trim(),
              routing_number: edit.routing_number.trim(),
              account_type: edit.account_type,
            },
          ],
        };
        const response = await ApiHttp.fetch(
          "recipients/bulk_update/",
          { method: "POST" },
          payload
        );
        const results = response as
          | { results: API.Recipient[] }
          | API.Recipient[];
        const created = Array.isArray(results)
          ? results[0]
          : results.results[0];
        setRowRecipients((prev) => ({ ...prev, [idx]: created }));
        setSelected((prev) => new Set(prev).add(idx));

        // Persist the created recipient on the nachaResponse row
        const origIdx = originalIndices[idx];
        updateNachaRows((rows) =>
          rows.map((r, i) => (i === origIdx ? { ...r, recipient: created } : r))
        );
      } catch (error) {
        if (error && typeof error === "object" && "errors" in error) {
          const bulkError = error as BulkUpdateErrorResponse;
          setRowErrors((prev) => {
            const next = { ...prev };
            bulkError.errors.forEach((rowErr) => {
              if (rowErr.index === 0) {
                next[idx] = { ...next[idx], ...rowErr.errors };
              }
            });
            return next;
          });
        }
        sendNotification({
          type: "negative",
          text: l10n.getString("recipient-review-bulk-update-error"),
        });
      } finally {
        autoCreatingRef.current.delete(idx);
      }
    },
    [l10n, sendNotification, originalIndices, updateNachaRows]
  );

  // --- Field editing handlers ---
  const handleFieldChange = (
    idx: number,
    field: keyof RowEdit,
    value: string
  ) => {
    setRowEdits((prev) => ({
      ...prev,
      [idx]: { ...prev[idx], [field]: value },
    }));

    // The value changed, so any prior backend `field_errors` entry for this
    // field is now stale. Replace it with the current client-side validation
    // result while preserving backend errors for untouched fields.
    const fieldKey = field === "recipient_name" ? "name" : field;
    const error = validateField(l10n, fieldKey, value);
    setRowErrors((prev) => {
      const nextRowErr = { ...(prev[idx] ?? {}) };
      if (error) {
        nextRowErr[fieldKey] = error;
      } else {
        delete nextRowErr[fieldKey];
      }
      return { ...prev, [idx]: nextRowErr };
    });
  };

  // Persist a row's errors, track dirty state, and auto-create a hidden
  // recipient only when the entire row is error-free (no client-side errors
  // and no remaining backend errors).
  const applyRowErrors = (
    idx: number,
    edit: RowEdit,
    nextRowErr: RowErrors
  ) => {
    setRowErrors((prev) => ({ ...prev, [idx]: nextRowErr }));

    if (rowRecipients[idx]) {
      setDirtyRows((prev) => new Set(prev).add(idx));
    }

    const clientErrors = validateRow(l10n, edit);
    const hasErrors =
      Object.keys(clientErrors).length > 0 ||
      Object.keys(nextRowErr).length > 0;
    if (!hasErrors && !rowRecipients[idx]) {
      autoCreateRecipient(idx, edit);
    }
  };

  const handleFieldBlur = (idx: number, field: keyof RowEdit) => {
    const edit = rowEdits[idx];
    if (!edit) return;

    const fieldKey = field === "recipient_name" ? "name" : field;
    const error = validateField(l10n, fieldKey, edit[field]);

    // Add-only: surface a client-side error when the value is invalid, but
    // never delete an existing error here. Deleting on a "valid" blur would
    // erase still-unresolved backend errors for a field the user never
    // actually changed (a backend error is client-valid by definition).
    const nextRowErr = { ...(rowErrors[idx] ?? {}) };
    if (error) {
      nextRowErr[fieldKey] = error;
    }
    applyRowErrors(idx, edit, nextRowErr);
  };

  // --- Select/deselect handlers ---
  const handleToggleSelectAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(unsavedIdxs));
    }
  };

  const handleToggleRow = (idx: number) => {
    if (saved.has(idx)) return;
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  // --- Save (un-hide) a single recipient ---
  const handleSaveRecipient = useCallback(
    async (idx: number) => {
      const recipient = rowRecipients[idx];
      if (!recipient || saved.has(idx) || saving.has(idx)) return;

      const edit = rowEdits[idx];
      const errors = edit ? validateRow(l10n, edit) : {};
      if (Object.keys(errors).length > 0) return;

      setSaving((prev) => new Set(prev).add(idx));
      try {
        const [resolved] = (await ApiHttp.fetch(
          "recipients/bulk_update/",
          { method: "POST" },
          {
            rows: [
              {
                uuid: recipient.id,
                name: edit.recipient_name.trim(),
                type: "business",
                account_number: edit.account_number.trim(),
                routing_number: edit.routing_number.trim(),
                account_type: edit.account_type,
              },
            ],
          }
        )) as API.Recipient[];
        const savedRecipient = (await ApiHttp.fetch(
          `recipients/${resolved.id}/`,
          { method: "PUT" },
          { name: resolved.name, type: "business", hidden: false }
        )) as API.Recipient;
        setRowRecipients((prev) => ({ ...prev, [idx]: savedRecipient }));
        const origIdx = originalIndices[idx];
        updateNachaRows((rows) =>
          rows.map((r, i) =>
            i === origIdx ? { ...r, recipient: savedRecipient } : r
          )
        );
        setDirtyRows((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
        setSaved((prev) => new Set(prev).add(idx));
        setSelected((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      } catch (error) {
        let errorText = l10n.getString("recipient-review-save-error");
        if (error && typeof error === "object" && "detail" in error) {
          errorText = (error as { detail: string }).detail;
        }
        sendNotification({
          type: "negative",
          text: errorText,
        });
      } finally {
        setSaving((prev) => {
          const next = new Set(prev);
          next.delete(idx);
          return next;
        });
      }
    },
    [
      rowRecipients,
      rowEdits,
      saved,
      saving,
      originalIndices,
      updateNachaRows,
      l10n,
      sendNotification,
    ]
  );

  const handleSaveAll = useCallback(async () => {
    const unsavedSelected = Array.from(selected).filter(
      (idx) => !saved.has(idx) && !saving.has(idx)
    );
    await Promise.allSettled(
      unsavedSelected.map((idx) => handleSaveRecipient(idx))
    );
  }, [selected, saved, saving, handleSaveRecipient]);

  const handleClearSelection = () => {
    setSelected(new Set());
  };

  const handleBack = () => {
    // Navigate to the explicit previous step rather than relying on browser
    // history (navigate(-1)), which is fragile once the user has moved forward
    // and back through the wizard. CSV imports arrive here from the column
    // mapping step; NACHA imports arrive directly from the file upload step.
    if (importType === "csv") {
      navigate("../map-columns");
    } else {
      navigate("..");
    }
  };

  // --- Next: bulk update dirty rows, then navigate ---
  const handleNext = useCallback(async () => {
    if (hasAnyErrors) return;

    const dirtyRowsArray = Array.from(dirtyRows).filter(
      (idx) => rowRecipients[idx]
    );

    if (dirtyRowsArray.length === 0) {
      navigate("../additional-details");
      return;
    }

    setNextLoading(true);
    try {
      const payload: { rows: BulkUpdateRow[] } = {
        rows: dirtyRowsArray.map((idx) => {
          const edit = rowEdits[idx];
          return {
            uuid: rowRecipients[idx].id,
            name: edit.recipient_name.trim(),
            type: "business",
            account_number: edit.account_number.trim(),
            routing_number: edit.routing_number.trim(),
            account_type: edit.account_type,
          };
        }),
      };
      const results = (await ApiHttp.fetch(
        "recipients/bulk_update/",
        { method: "POST" },
        payload
      )) as API.Recipient[];

      // Sync updated recipients back to nachaResponse rows
      updateNachaRows((rows) => {
        const updated = [...rows];
        dirtyRowsArray.forEach((idx, i) => {
          const origIdx = originalIndices[idx];
          updated[origIdx] = { ...updated[origIdx], recipient: results[i] };
        });
        return updated;
      });

      setDirtyRows(new Set());
      navigate("../additional-details");
    } catch (error) {
      if (error && typeof error === "object" && "errors" in error) {
        const bulkError = error as BulkUpdateErrorResponse;
        const dirtyArr = Array.from(dirtyRows).filter(
          (idx) => rowRecipients[idx]
        );
        setRowErrors((prev) => {
          const next = { ...prev };
          bulkError.errors.forEach((rowErr) => {
            const realIdx = dirtyArr[rowErr.index];
            if (realIdx !== undefined) {
              next[realIdx] = {
                ...next[realIdx],
                ...rowErr.errors,
              };
            }
          });
          return next;
        });
      }
      sendNotification({
        type: "negative",
        text: l10n.getString("recipient-review-bulk-update-error"),
      });
    } finally {
      setNextLoading(false);
    }
  }, [
    hasAnyErrors,
    dirtyRows,
    rowRecipients,
    rowEdits,
    originalIndices,
    updateNachaRows,
    navigate,
    l10n,
    sendNotification,
  ]);

  // --- Tag helpers ---
  const getTagClassName = (idx: number, tagStyles: Record<string, string>) => {
    if (saved.has(idx)) return tagStyles.saveTagSaved;
    if (saving.has(idx)) return tagStyles.saveTagSaving;
    return tagStyles.saveTag;
  };

  const getTagLabel = (idx: number) => {
    if (saved.has(idx)) {
      return l10n.getString("recipient-review-added-to-recipients");
    }
    return l10n.getString("recipient-review-add-to-recipients");
  };

  const canSaveRow = (idx: number): boolean => {
    const errors = rowErrors[idx];
    const hasErrors = errors && Object.keys(errors).length > 0;
    return (
      !hasErrors &&
      rowRecipients[idx] !== undefined &&
      !saved.has(idx) &&
      !saving.has(idx)
    );
  };

  // --- Handle account_type select change (inline) ---
  // A select change is an atomic commit and always represents a value change,
  // so validate immediately and clear any stale error for the field.
  const handleAccountTypeChange = (idx: number, value: string) => {
    const edit = { ...rowEdits[idx], account_type: value };
    setRowEdits((prev) => ({ ...prev, [idx]: edit }));

    const error = validateField(l10n, "account_type", value);
    const nextRowErr = { ...(rowErrors[idx] ?? {}) };
    if (error) {
      nextRowErr.account_type = error;
    } else {
      delete nextRowErr.account_type;
    }
    applyRowErrors(idx, edit, nextRowErr);
  };

  return {
    l10n,
    nachaResponse,
    newRecipientRows,
    rowEdits,
    rowErrors,
    rowRecipients,
    selected,
    saved,
    saving,
    nextLoading,
    hasAnyErrors,
    errorCategories,
    allSelected,
    someSelected,
    handleFieldChange,
    handleFieldBlur,
    handleToggleSelectAll,
    handleToggleRow,
    handleSaveRecipient,
    handleSaveAll,
    handleClearSelection,
    handleBack,
    handleNext,
    handleAccountTypeChange,
    getTagClassName,
    getTagLabel,
    canSaveRow,
  };
};
